import { useRef, useState } from "react";
import { Upload, ImageIcon, CheckCircle2, XCircle } from "lucide-react";
import { getToken } from "@/lib/api";
import { toast } from "sonner";

/**
 * Uploads a CampMinder "Photo Report" .xlsx and populates staff photos on
 * every matching crew_member (matched by PersonID). Existing photos are
 * NEVER overwritten — this panel only fills in blanks.
 *
 * Renders a summary after upload: matched, uploaded, skipped_no_person,
 * already_had_photo, and any errors.
 */
export default function StaffPhotoImportPanel() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/admin/photos/upload-staff-xlsx`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.detail || `Upload failed (HTTP ${res.status})`);
        setBusy(false);
        return;
      }
      setResult(body);
      toast.success(
        `Uploaded ${body.uploaded} photo${body.uploaded === 1 ? "" : "s"}` +
        (body.skipped_already_had_photo
          ? ` (${body.skipped_already_had_photo} already had photos)`
          : "")
      );
    } catch (e) {
      toast.error(`Upload failed: ${e.message || e}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="staff-photo-import-panel">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        <p className="font-semibold text-slate-900 mb-1">How this works</p>
        <ol className="list-decimal ml-4 space-y-1">
          <li>In CampMinder, run the <b>Staff Photo Report</b> (columns: PersonID, Last Name, First Name, Staff Picture).</li>
          <li>Export as <b>.xlsx</b>. The Staff Picture column can be <b>either embedded images or photo URLs</b> — both work.</li>
          <li>Upload it here. We match each row by <b>PersonID</b> and add the picture to the matching staff card + dashboard row.</li>
          <li>Existing photos are never overwritten — this only fills in blanks.</li>
        </ol>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="staff-photo-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white font-semibold"
          data-testid="staff-photo-upload-btn"
        >
          <Upload className="w-4 h-4" />
          {busy ? "Uploading…" : "Choose CampMinder .xlsx"}
        </button>
        <span className="text-xs text-slate-500">
          Large reports may take 30–60 s to process — please don&apos;t close the tab.
        </span>
      </div>

      {result && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-2"
          data-testid="staff-photo-upload-result"
        >
          <div className="flex items-center gap-2 font-semibold text-emerald-900">
            <CheckCircle2 className="w-5 h-5" />
            Upload complete — <span data-testid="upload-result-filename">{result.filename}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-slate-800">
            <Metric label="Rows in report" value={result.total_rows} testId="metric-total-rows" />
            <Metric
              label="Photos in sheet"
              value={result.images_found}
              testId="metric-images-found"
              hint={
                result.urls_found > 0 && result.images_embedded > 0
                  ? `${result.urls_found} URLs · ${result.images_embedded} embedded`
                  : result.urls_found > 0
                  ? `${result.urls_found} URLs`
                  : result.images_embedded > 0
                  ? `${result.images_embedded} embedded`
                  : null
              }
            />
            <Metric label="Matched to staff" value={result.matched} testId="metric-matched" />
            <Metric
              label="Photos uploaded"
              value={result.uploaded}
              tone="good"
              testId="metric-uploaded"
            />
            <Metric
              label="Already had photo"
              value={result.skipped_already_had_photo}
              testId="metric-already"
            />
            <Metric
              label="No matching staff"
              value={result.skipped_no_person + (result.unmatched_person_ids_count || 0)}
              tone={
                result.skipped_no_person + (result.unmatched_person_ids_count || 0) > 0
                  ? "warn"
                  : "neutral"
              }
              testId="metric-unmatched"
            />
          </div>
          {(result.unmatched_person_ids_count || 0) > 0 && (
            <div className="text-xs text-amber-800 pt-1">
              <span className="font-semibold">Unmatched PersonIDs</span> (first 50):{" "}
              <span className="font-mono">{(result.unmatched_person_ids || []).join(", ")}</span>
              {result.unmatched_person_ids_count > 50 && ` … +${result.unmatched_person_ids_count - 50} more`}
            </div>
          )}
          {(result.errors_count || 0) > 0 && (
            <div className="text-xs text-red-800 pt-1 flex items-start gap-1.5">
              <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">
                  {result.errors_count} row error{result.errors_count === 1 ? "" : "s"}:
                </span>{" "}
                {(result.errors || []).slice(0, 3).join(" | ")}
                {result.errors_count > 3 && ` … (${result.errors_count - 3} more)`}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-slate-500">
        <ImageIcon className="w-3.5 h-3.5" />
        Photos are resized to 400×400 (staff cards) and 96×96 (dashboard avatars) and stored in the roster database.
      </div>
    </div>
  );
}

function Metric({ label, value, tone = "neutral", testId, hint = null }) {
  const toneClass =
    tone === "good"
      ? "text-emerald-700"
      : tone === "warn"
      ? "text-amber-700"
      : "text-slate-800";
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`text-lg font-bold ${toneClass}`}>{value ?? 0}</div>
      {hint && <div className="text-[10px] text-slate-500 font-medium">{hint}</div>}
    </div>
  );
}
